import React, { useContext } from "react";
import { Bar, Line } from "react-chartjs-2";
import { PrintContext } from "../../ChartApp";
import { AnimatePresence, motion } from "framer-motion";
import { convertToPercent, getChartDataIndiMulti, getChartDataSocial, getChartDataZite, getChartDataPite, legendPaddingIncreasePlugin, makeKey, roundToFourDecimalsIfNeeded, } from "../../utilities";
import passIcon from "../../../images/icons/pass.svg";
import { formatChemicalFormulas } from "../../utilities";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, } from "chart.js";
// Register the necessary components with Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, legendPaddingIncreasePlugin);
const ChartCommon = ({ pageType, multiSelects, chosenCategory, chosenValue, mockDataCategory, optionsSelected, isStacked, selectedChart, area, }) => {
    const printRef = useContext(PrintContext);
    const xAxisHeight = 20;
    const yAxisWidth = 175;
    const getChartOptions = (data, isStacked) => {
        return {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: pageType === "social_data" ? false : true,
                    labels: {
                        font: {
                            size: 16,
                        },
                    },
                },
            },
            responsive: true,
            scales: {
                x: {
                    stacked: selectedChart === "bar-chart" ? isStacked : false,
                    grid: {
                        display: false,
                    },
                    offset: true, // Offset the x-axis to start and end in the middle of the labels
                    ticks: {
                        autoSkip: selectedChart === "bar-chart" ? true : false, // Ensures all labels are displayed
                    },
                    afterFit: function (axis) {
                        axis.height = xAxisHeight;
                    },
                },
                y: {
                    stacked: selectedChart === "bar-chart" ? isStacked : false,
                    ticks: {
                        callback: function (value) {
                            let val = pageType === "social_data" ? convertToPercent(value) : value;
                            return val;
                        },
                        font: {
                            size: 18,
                        },
                    },
                    afterFit: function (axis) {
                        axis.width = yAxisWidth;
                    },
                },
            },
        };
    };
    const getChartData = (chartData, optionSelected, pageType) => {
        if (pageType === "environmental_indicators_data" ||
            pageType === "economic_jmi_data" ||
            pageType === "economic_jmo_data") {
            let data = getChartDataIndiMulti(chartData, optionSelected, selectedChart, pageType);
            return data;
        }
        else if (pageType === "economic_zite_data") {
            let data = getChartDataZite(chartData, selectedChart, multiSelects);
            return data;
        }
        else if (pageType === "economic_pite_data") {
            let data = getChartDataPite(chartData, selectedChart, multiSelects);
            return data;
        }
        else if (pageType === "social_data") {
            let data = getChartDataSocial(multiSelects, chartData, selectedChart, chosenCategory.chosenCategoryId === "wskazniki-dla-polski");
            return data;
        }
    };
    const chartData = chosenCategory
        ? getChartData(mockDataCategory === null || mockDataCategory === void 0 ? void 0 : mockDataCategory.values.find((value) => value.id === chosenValue.chosenValueId), optionsSelected, pageType)
        : undefined;
    const chartOptions = mockDataCategory
        ? getChartOptions(mockDataCategory.values.find((value) => value.id === chosenValue.chosenValueId), isStacked)
        : undefined;
    const getChartType = (selectedChart, chartData, chartOptions) => {
        let chartType = React.createElement(Bar, { data: chartData, options: chartOptions });
        let chartDataDeleteLabels = {
            labels: new Array([...chartData.labels].flatMap((item) => item.label).length).fill(""),
            datasets: [...chartData.datasets],
        };
        if (selectedChart === "bar-chart") {
            chartType = React.createElement(Bar, { data: chartDataDeleteLabels, options: chartOptions });
        }
        else if (selectedChart === "line-chart") {
            chartType = React.createElement(Line, { data: chartDataDeleteLabels, options: chartOptions });
        }
        else {
            chartType = React.createElement(Bar, { data: chartDataDeleteLabels, options: chartOptions });
        }
        return chartType;
    };
    let chart = getChartType(selectedChart, chartData, chartOptions);
    const getChartLabel = () => {
        let label = "";
        if (pageType === "economic_jmi_data" || pageType === "economic_jmo_data") {
            const bracketIndex = chosenValue.chosenValueLabel.indexOf("[");
            const metricName = bracketIndex > 0
                ? chosenValue.chosenValueLabel.substring(0, bracketIndex).trim()
                : chosenValue.chosenValueLabel;
            label = `${chosenCategory.chosenCategoryLabel} - ${metricName}`;
        }
        else if (pageType === "environmental_indicators_data") {
            label = `Wskaźniki emisji zanieczyszczeń gospodarstw domowych - ${chosenValue.chosenValueLabel}`;
        }
        else if (pageType === "social_data") {
            const areaName = area.value === "polska" ? "Polska" : "Województwa";
            label = `Wskaźnik ubóstwa energetycznego - ${chosenValue.chosenValueLabel} (${areaName})`;
        }
        else {
            label = chosenValue.chosenValueLabel;
        }
        return label;
    };
    const isHeadersNumbers = chartData &&
        pageType === "social_data" &&
        !isNaN(parseFloat(chartData.labels));
    return (React.createElement("section", { className: "chartBox card", ref: printRef, style: {
            "--firstCellWidth": `${yAxisWidth}px`,
        } },
        React.createElement("h3", { className: "text-gray-300" }, formatChemicalFormulas(getChartLabel())),
        React.createElement("div", { className: "bar" },
            React.createElement("div", { className: "chart position-relative", style: { top: `${xAxisHeight}px` } }, chart),
            React.createElement("img", { id: "printImg", className: "d-none w-100" }),
            React.createElement(AnimatePresence, null, chartData && chartData.labels.length !== 0 ? (React.createElement(motion.table, { key: "dataTable", className: "chartData dataTable", initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.5 } },
                React.createElement("thead", null,
                    React.createElement("tr", { className: "tableRow" },
                        React.createElement("th", { className: "firstCell noStyle" }),
                        chartData.labels.map((header) => {
                            return (React.createElement(motion.th, { key: makeKey(), initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 1 }, className: pageType === "social_data" &&
                                    header !== "brak danych" &&
                                    !isHeadersNumbers
                                    ? "text-vertical"
                                    : "" },
                                React.createElement("span", null, header)));
                        }))),
                React.createElement("tbody", null, chartData === null || chartData === void 0 ? void 0 : chartData.datasets.map(({ label, data }) => (React.createElement(motion.tr, { key: makeKey(), className: "tableRow" },
                    React.createElement("td", { className: `firstCell ${isHeadersNumbers ? "firstCell--hidden" : ""}` }, label),
                    data.map((value) => {
                        let ultimateVal = pageType !== "social_data"
                            ? roundToFourDecimalsIfNeeded(value)
                            : convertToPercent(+value);
                        return (React.createElement(motion.td, { key: makeKey(), initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 1 }, className: pageType === "social_data" ? "fs-0875" : "default" }, value === "brak danych" || value === null
                            ? "brak danych"
                            : ultimateVal));
                    }))))))) : (React.createElement(motion.table, { key: "infoTable", className: "chartData infoTable", initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.5 } },
                React.createElement("thead", null,
                    React.createElement("tr", { className: "tableRow__noTable" },
                        React.createElement("td", { className: "firstCell noStyle" }),
                        React.createElement("td", { className: "tableInfo" },
                            "Wybierz parametry powy\u017Cej, aby wyswietli\u0107 statystyki",
                            React.createElement("span", { className: "icon" },
                                React.createElement("img", { src: passIcon, alt: "passIcon" })))))))))));
};
export default ChartCommon;
